import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;

const sides = {
  right:
    'inset-y-0 right-0 h-full w-[420px] max-w-[calc(100vw-48px)] border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  left: 'inset-y-0 left-0 h-full w-[420px] max-w-[calc(100vw-48px)] border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  top: 'inset-x-0 top-0 w-full border-b data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
  bottom:
    'inset-x-0 bottom-0 w-full border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
} as const;

export interface SheetContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, 'title'> {
  side?: keyof typeof sides;
  title?: React.ReactNode;
  description?: React.ReactNode;
  showClose?: boolean;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ className, side = 'right', title, description, showClose = true, children, ...props }, ref) => {
  const { t } = useI18n();

  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/20 backdrop-blur-[2px]" />
      <SheetPrimitive.Content
        ref={ref}
        {...(description ? {} : { 'aria-describedby': undefined })}
        // Entry direction follows the side: an edge panel that slid up would read
        // as a dialog that landed in the wrong place.
        className={cn(
          'fixed z-50 flex flex-col border-line bg-surface shadow-pop',
          'duration-150 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out',
          sides[side],
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <SheetPrimitive.Title className={cn('text-md font-medium text-ink', !title && 'sr-only')}>
              {title ?? t('sheet.panel')}
            </SheetPrimitive.Title>
            {description ? (
              <SheetPrimitive.Description className="mt-1 text-sm text-ink-muted">
                {description}
              </SheetPrimitive.Description>
            ) : null}
          </div>
          {showClose ? (
            <SheetPrimitive.Close
              aria-label={t('action.close')}
              className="-mr-1 inline-flex size-[26px] shrink-0 items-center justify-center rounded text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
            >
              <X className="size-3.5" aria-hidden="true" />
            </SheetPrimitive.Close>
          ) : null}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-base">{children}</div>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
  },
);
SheetContent.displayName = 'SheetContent';

const SheetFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-end gap-2 border-t border-line px-4 py-3', className)}
      {...props}
    />
  ),
);
SheetFooter.displayName = 'SheetFooter';

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetFooter };
